import type { TabView } from "@e308/ux";
import { pactFor } from "../diplomacy-state.js";
import type { SeatObservation } from "../types.js";
import type { AppIntent, AppState } from "./app-source.js";
import { dashboard } from "./layout-view.js";
import {
  appAction,
  formatGameClock as clock,
  gameAction,
  gameIntent,
  heading,
  textInput as input,
  type AppNode as Node,
  name,
  text,
} from "./view-nodes.js";

export function valleyTab(state: AppState): TabView<AppIntent, string> {
  const observation = state.observation as SeatObservation;
  const neighbors = Object.keys(observation.self.logistics.borders);
  const target = neighbors.includes(state.valleyDraft.target)
    ? state.valleyDraft.target
    : (neighbors[0] ?? "");
  return {
    id: "valley",
    label: "Valley",
    content: dashboard(
      [
        heading("valley-heading", "Valley"),
        ...dangerAlerts(observation),
        mapView(observation),
        text(
          "adjacency-text",
          neighbors.length > 0
            ? `${observation.self.name} is adjacent to ${neighbors.map((id) => seatName(observation, id)).join(", ")}.`
            : `${observation.self.name} has no surviving neighbors.`,
        ),
        ...newBorderNotices(state),
        heading("town-inspections", "Open town inspections", 3),
        ...observation.seats.map((seat) => inspection(seat, observation)),
        heading("diplomacy-heading", "Local diplomacy", 3),
        targetSelect(target, neighbors),
        diplomacyPanel(state, target),
        shipmentList(observation),
        heading("chat-heading", "Practice chat", 3),
        text(
          "chat-local-note",
          "Practice notes stay on this device. To chat with other players, join an online room.",
        ),
        input("chat-message", "Message", state.valleyDraft.chatMessage, (value) => ({
          kind: "set-valley-draft",
          change: { chatMessage: value },
        })),
        appAction("send-chat", "Send local message", { kind: "send-local-chat" }),
        ...state.chat.map((message) =>
          text(
            `chat-${message.id}`,
            `${clock(message.atGameMs)} — ${message.sender}: ${message.text}`,
          ),
        ),
        heading("artifacts-heading", "Save and replay artifacts", 3),
        artifactControls(state),
      ],
      ["diplomacy-heading", "town-inspections"],
    ),
  };
}

function dangerAlerts(observation: SeatObservation): readonly Node[] {
  return Object.keys(observation.self.logistics.borders).flatMap((id) => {
    const enemy = observation.seats.find((seat) => seat.id === id);
    const order = enemy?.logistics.borders[observation.self.id]?.standingOrder;
    if (order !== "assault" && order !== "raid") return [];
    const peace = peaceRemaining(observation, id);
    return [
      {
        kind: "notification",
        id: `danger-${id}`,
        tone: "danger",
        text:
          peace > 0
            ? `${seatName(observation, id)} has ${name(order)} queued; peace ends in ${Math.ceil(peace / 1_000)} game seconds.`
            : `${seatName(observation, id)} is attacking with ${name(order)}.`,
      },
    ];
  });
}

function mapView(observation: SeatObservation): Node {
  return {
    kind: "tree",
    id: "valley-map",
    nodes: observation.seats.map((seat, index) => ({
      id: seat.id,
      label: seat.name,
      x: 60 + (index % 4) * 140,
      y: 60 + Math.floor(index / 4) * 100,
      mark: {
        label: seat.alive ? "Active" : "Eliminated",
        tone: seat.alive ? "positive" : "danger",
      },
    })),
    branches: observation.match.borders.map((border) => ({ from: border.left, to: border.right })),
  };
}

function newBorderNotices(state: AppState): readonly Node[] {
  const result: Node[] = [];
  for (const event of state.events.slice(-20)) {
    if (event.kind === "new-border" && event.seatId === state.observation?.self.id)
      result.push(
        text(
          `valley-event-${event.sequence}`,
          `${clock(event.atGameMs)} — New neighbor ${event.borderId && state.observation ? seatName(state.observation, event.borderId) : "unknown"}; local peace ends at ${clock(event.fightingStartsAtMs ?? event.atGameMs)}.`,
        ),
      );
    else if (event.kind === "seat-eliminated")
      result.push(
        text(
          `valley-event-${event.sequence}`,
          `${clock(event.atGameMs)} — ${event.seatId && state.observation ? seatName(state.observation, event.seatId) : "A town"} was eliminated.`,
        ),
      );
  }
  return result.slice(-5);
}

function inspection(seat: SeatObservation["self"], observation: SeatObservation): Node {
  return {
    kind: "infobox",
    id: `inspect-${seat.id}`,
    title: `${seat.name} — ${seat.alive ? "active" : "eliminated"}`,
    content: [
      text(
        `inspect-${seat.id}-stocks`,
        `Headquarters ${seat.headquartersIntegrity}; Metal ${seat.metal}; RP ${seat.researchPoints}. Units: ${Object.entries(
          seat.units,
        )
          .map(([id, value]) => `${name(id)} ${value}`)
          .join(", ")}.`,
      ),
      text(
        `inspect-${seat.id}-buildings`,
        `Buildings: ${Object.entries(seat.buildings)
          .map(([id, value]) => `${name(id)} ${value}`)
          .join(", ")}.`,
      ),
      text(
        `inspect-${seat.id}-research`,
        `Research: ${seat.research.completed.map(name).join(", ") || "none"}; active ${seat.research.active ? name(seat.research.active.order.target) : "none"}.`,
      ),
      text(
        `inspect-${seat.id}-borders`,
        `Borders: ${
          Object.entries(seat.logistics.borders)
            .map(([id, border]) => `${seatName(observation, id)} (${name(border.standingOrder)})`)
            .join(", ") || "none"
        }.`,
      ),
    ],
  };
}

function diplomacyPanel(state: AppState, target: string): Node {
  const observation = state.observation as SeatObservation;
  const pact = pactFor(observation.diplomacy, observation.self.id, target);
  const incoming = pact?.proposals.some((proposal) => proposal.from === target);
  const status = pact?.status ?? "none";
  return {
    kind: "stack",
    id: "diplomacy-controls",
    children: [
      text(
        "pact-status",
        `Pact status with ${seatName(observation, target)}: ${name(status)}${pact?.noticeEndsAtMs ? `; notice ends in ${Math.max(0, Math.ceil((pact.noticeEndsAtMs - observation.gameTimeMs) / 1_000))} game seconds` : ""}.`,
      ),
      input("pact-message", "Optional pact message", state.valleyDraft.pactMessage, (value) => ({
        kind: "set-valley-draft",
        change: { pactMessage: value },
      })),
      gameAction("propose-pact", "Propose non-aggression pact", {
        kind: "propose-pact",
        targetSeatId: target,
        ...(state.valleyDraft.pactMessage ? { message: state.valleyDraft.pactMessage } : {}),
      }),
      ...(incoming
        ? [gameAction("accept-pact", "Accept pact", { kind: "accept-pact", targetSeatId: target })]
        : []),
      ...(status === "active"
        ? [
            {
              kind: "action",
              id: "end-pact",
              action: {
                id: "end-pact",
                label: "Issue 60-second pact notice",
                enabled: true,
                intent: gameIntent("end-pact", {
                  kind: "end-pact",
                  targetSeatId: target,
                  confirmed: true,
                }),
                blockers: [],
                confirm: "End this pact? Both towns receive 60 game seconds of public notice.",
              },
            } as Node,
          ]
        : []),
      input(
        "shipment-amount",
        "Metal shipment amount",
        state.valleyDraft.shipmentAmount,
        (value) => ({ kind: "set-valley-draft", change: { shipmentAmount: value } }),
      ),
      {
        kind: "action",
        id: "send-metal",
        action: {
          id: "send-metal",
          label: `Send ${state.valleyDraft.shipmentAmount || "0"} Metal`,
          enabled: true,
          intent: gameIntent("send-metal", {
            kind: "send-metal",
            targetSeatId: target,
            amount: state.valleyDraft.shipmentAmount,
            confirmed: true,
          }),
          blockers: [],
          confirm: "Send this Metal now? It is removed immediately and cannot be recalled.",
        },
      },
    ],
  };
}

function shipmentList(observation: SeatObservation): Node {
  return {
    kind: "infobox",
    id: "shipments",
    title: `Visible Metal shipments — ${observation.diplomacy.shipments.length}`,
    content:
      observation.diplomacy.shipments.length === 0
        ? [text("shipments-empty", "No visible shipments.")]
        : observation.diplomacy.shipments.map((shipment) =>
            text(
              `shipment-${shipment.id}`,
              `${shipment.amount} Metal: ${seatName(observation, shipment.senderId)} → ${seatName(observation, shipment.recipientId)}; ${name(shipment.status)}${shipment.status === "in-transit" ? ` in ${Math.max(0, Math.ceil((shipment.deliversAtMs - observation.gameTimeMs) / 1_000))} game seconds` : ""}.`,
            ),
          ),
  };
}

function artifactControls(state: AppState): Node {
  return {
    kind: "stack",
    id: "artifact-controls",
    children: [
      {
        kind: "save",
        id: "save-actions",
        status: state.artifact ? "saved" : "clean",
        message: state.artifact
          ? `${name(state.artifact.kind)} artifact ready to copy.`
          : "Exports are checksummed JSON and grant zero offline production.",
        export: {
          id: "export-save",
          label: "Export save",
          enabled: true,
          blockers: [],
          intent: { kind: "export-artifact", artifactKind: "save" },
        },
        save: {
          id: "export-replay",
          label: "Export replay",
          enabled: true,
          blockers: [],
          intent: { kind: "export-artifact", artifactKind: "replay" },
        },
        import: {
          id: "import-save",
          label: "Import pasted save",
          enabled: true,
          blockers: [],
          intent: { kind: "import-artifact", artifactKind: "save" },
          confirm:
            "Replace the current local match with this save? No wall-time production will be granted.",
        },
      },
      input(
        "artifact-input",
        "Paste save or replay JSON",
        state.valleyDraft.importArtifact,
        (value) => ({ kind: "set-valley-draft", change: { importArtifact: value } }),
      ),
      appAction("import-replay", "Inspect pasted replay result", {
        kind: "import-artifact",
        artifactKind: "replay",
      }),
      ...(state.replayActive
        ? [appAction("exit-replay", "Return to unchanged live result", { kind: "exit-replay" })]
        : []),
      ...(state.artifact
        ? [
            input(
              "artifact-output",
              `${name(state.artifact.kind)} JSON`,
              state.artifact.value,
              () => ({ kind: "set-valley-draft", change: {} }),
            ),
          ]
        : []),
    ],
  };
}

function targetSelect(value: string, neighbors: readonly string[]): Node {
  return {
    kind: "select-input",
    id: "diplomacy-target",
    label: "Neighbor",
    value,
    options: neighbors.map((id) => ({ value: id, label: name(id) })),
    intent: (target) => ({ kind: "set-valley-draft", change: { target } }),
  };
}

function peaceRemaining(observation: SeatObservation, neighborId: string): number {
  const border = observation.match.borders.find(
    (entry) =>
      (entry.left === observation.self.id && entry.right === neighborId) ||
      (entry.right === observation.self.id && entry.left === neighborId),
  );
  return Math.max(0, (border?.fightingStartsAtMs ?? 0) - observation.gameTimeMs);
}

function seatName(observation: SeatObservation, id: string): string {
  return observation.seats.find((seat) => seat.id === id)?.name ?? name(id);
}
